// TrainingCalendar.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Table, Button, Badge } from 'react-bootstrap';
import { getUser } from '../utils/auth';

const STATUS_VARIANT = {
  Scheduled: 'primary',
  Done: 'success',
  Canceled: 'danger',
  Postponed: 'warning',
};

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Build a local-time YYYY-MM-DD string. Not toISOString(), which converts to
// UTC first and can shift the date by a day depending on local timezone offset.
function toLocalDateString(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Parse a "YYYY-MM-DD" (optionally with a time suffix) string as a local
// Date, not new Date(str)'s UTC-midnight interpretation of date-only ISO
// strings — the same skew toLocalDateString avoids, just in reverse.
function parseLocalDate(dateStr) {
  const [year, month, day] = dateStr.split(/[T ]/)[0].split('-').map(Number);
  return new Date(year, month - 1, day);
}

function TrainingCalendar() {
  const loggedInUser = getUser();
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const monthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    const start = toLocalDateString(monthStart);
    const end = toLocalDateString(monthEnd);

    setLoading(true);
    fetch(`${API_BASE_URL}/trainings/get_all_trainings.php?filter=range&start=${start}&end=${end}&user_id=${loggedInUser.id}`, { credentials: 'include' })
      .then(response => {
        if (!response.ok) throw new Error('Failed to fetch trainings');
        return response.json();
      })
      .then(data => {
        setTrainings(Array.isArray(data.trainings) ? data.trainings : []);
        setLoading(false);
      })
      .catch(err => {
        console.error('Error fetching calendar trainings:', err);
        setError(err.message);
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentMonth]);

  const monthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
  const monthEnd = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
  const daysInMonth = monthEnd.getDate();
  const leadingBlanks = monthStart.getDay();

  // { [dayOfMonth]: training[] } — a multi-day training appears under every
  // day it spans within the visible month.
  const entriesByDay = {};
  trainings.forEach(training => {
    const tStart = parseLocalDate(training.start_date);
    const tEnd = parseLocalDate(training.end_date);
    const spanStart = tStart < monthStart ? monthStart : tStart;
    const spanEnd = tEnd > monthEnd ? monthEnd : tEnd;
    for (let d = new Date(spanStart); d <= spanEnd; d.setDate(d.getDate() + 1)) {
      const day = d.getDate();
      if (!entriesByDay[day]) entriesByDay[day] = [];
      entriesByDay[day].push(training);
    }
  });

  const cells = [];
  for (let i = 0; i < leadingBlanks; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) cells.push(day);
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }

  const goToPrevMonth = () => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  const goToNextMonth = () => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));

  const monthLabel = currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' });

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <Button onClick={goToPrevMonth}>Previous</Button>
        <h2 className="mb-0">{monthLabel}</h2>
        <Button onClick={goToNextMonth}>Next</Button>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : error ? (
        <p>Error: {error}</p>
      ) : (
        <Table bordered>
          <thead>
            <tr>
              {WEEKDAY_LABELS.map(label => (
                <th key={label} className="text-center">{label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {weeks.map((week, weekIndex) => (
              <tr key={weekIndex}>
                {week.map((day, dayIndex) => (
                  <td key={dayIndex} style={{ verticalAlign: 'top', height: '110px', width: '14.28%' }}>
                    {day && (
                      <>
                        <div className="fw-bold">{day}</div>
                        {(entriesByDay[day] || []).map(training => (
                          <div key={training.id} className="mb-1">
                            <Link
                              to={`/training/view/${training.id}`}
                              className="text-decoration-none"
                              title={training.title}
                            >
                              <Badge
                                bg={STATUS_VARIANT[training.status] || 'secondary'}
                                className="d-block text-start text-truncate"
                                style={{ maxWidth: '100%' }}
                              >
                                {training.code}
                              </Badge>
                            </Link>
                          </div>
                        ))}
                      </>
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </div>
  );
}

export default TrainingCalendar;
